import { useEffect, useState } from 'react';
import { Database, KeyRound, Loader2, Play, Search, ShieldAlert, Sparkles, UserPlus, Wrench } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DataCleanupTool } from './DataCleanupTool';
import { SessionDebugger } from './SessionDebugger';
import { ManualSubdomainCreator } from './ManualSubdomainCreator';
import { AIUsageMonitor } from './ai/AIUsageMonitor';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';

/**
 * System Admin › Operations (RESTORE_PLAN R3.6): the operator tools that lived
 * on the retired SystemAdminDashboard, AccountDiagnostic, AdminTools,
 * DatabaseDiagnostic and DataDiagnostic pages, in one place. Every route here
 * is behind the platform's system-admin gate.
 */

const API = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
export const SEED_TEMPLATE_IDS = ['atlassian', 'servicenow', 'ivanti', 'bmc-helix', 'freshservice'];

/** A sign-in password no one sees: the person sets their own with "Forgot password" (the old tool sent a fixed one). */
export function unguessablePassword(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return `${Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('')}Aa1!`;
}

function Section({ icon: Icon, title, description, children }: { icon: any; title: string; description: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Icon className="size-5" aria-hidden="true" />{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function AccountRepair({ accessToken }: { accessToken: string }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  const check = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setBusy(true); setError(''); setResult(null);
    try {
      const res = await fetch(`${API}/admin/check-account`, { method: 'POST', headers: getAuthHeadersWithJson(accessToken), body: JSON.stringify({ email: email.toLowerCase().trim() }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not check the account');
      setResult(body);
    } catch (err: any) { setError(err?.message || 'Could not check the account'); } finally { setBusy(false); }
  };

  const createSignIn = async () => {
    if (!window.confirm(`Create a sign-in account for ${email}? They set their password with "Forgot password" on the sign-in page.`)) return;
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/admin/create-auth-account`, { method: 'POST', headers: getAuthHeadersWithJson(accessToken), body: JSON.stringify({ email: email.toLowerCase().trim(), password: unguessablePassword() }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not create the sign-in account');
      toast.success(`Sign-in account created for ${email}. Ask them to use "Forgot password" to set a password.`);
      await check();
    } catch (err: any) { setError(err?.message || 'Could not create the sign-in account'); setBusy(false); }
  };

  return (
    <Section icon={KeyRound} title="Account lookup and repair" description="Find a person by email across the sign-in store and the user records; create a missing sign-in account">
      <form onSubmit={check} className="flex gap-2">
        <Input aria-label="Email to look up" type="email" placeholder="person@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="max-w-sm" />
        <Button type="submit" disabled={busy || !email.trim()}>{busy ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Search className="size-4 mr-2" />}Check</Button>
      </form>
      {error && <p className="text-red-600" role="alert">{error}</p>}
      {result && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="account-result">
          <div className="rounded border p-3">
            <p>Sign-in account</p>
            {result.authAccount ? <p className="text-muted-foreground">{result.authAccount.email} · created {result.authAccount.created ? new Date(result.authAccount.created).toLocaleDateString() : '—'}</p> : (
              <>
                <p className="text-red-700">Missing</p>
                <Button size="sm" variant="outline" className="mt-2" onClick={createSignIn} disabled={busy}><UserPlus className="size-4 mr-2" />Create sign-in account</Button>
              </>
            )}
          </div>
          <div className="rounded border p-3">
            <p>User record</p>
            {result.kvUser ? <p className="text-muted-foreground">{result.kvUser.name || '—'} · {result.kvUser.role} · {result.kvUser.status || 'active'}</p> : <p className="text-red-700">Missing</p>}
          </div>
          <div className="rounded border p-3">
            <p>Company</p>
            {result.company ? <p className="text-muted-foreground">{result.company.name}{result.company.subdomain ? ` · ${result.company.subdomain}` : ''}</p> : <p className="text-muted-foreground">None</p>}
          </div>
        </div>
      )}
    </Section>
  );
}

function PlatformJobs({ accessToken }: { accessToken: string }) {
  const [running, setRunning] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const note = (line: string) => setLog((l) => [line, ...l].slice(0, 20));

  const call = async (path: string, method: 'GET' | 'POST', body?: unknown) => {
    const res = await fetch(`${API}${path}`, { method, headers: body ? getAuthHeadersWithJson(accessToken) : getAuthHeaders(accessToken), body: body ? JSON.stringify(body) : undefined });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `${path} failed (${res.status})`);
    return data;
  };

  const run = async (id: string, question: string | null, job: () => Promise<string>) => {
    if (question && !window.confirm(question)) return;
    setRunning(id);
    try { note(await job()); } catch (e: any) { note(`Failed: ${e?.message || id}`); toast.error(e?.message || 'The job failed'); } finally { setRunning(null); }
  };

  const jobs: { id: string; label: string; question: string | null; job: () => Promise<string> }[] = [
    {
      id: 'seed', label: 'Seed template config items', question: `Seed the template config items and questions for all ${SEED_TEMPLATE_IDS.length} templates?`,
      job: async () => {
        const done: string[] = [];
        for (const templateId of SEED_TEMPLATE_IDS) { await call('/system-admin/seed-template-cis', 'POST', { templateId }); done.push(templateId); }
        return `Seeded ${done.join(', ')}`;
      },
    },
    {
      id: 'cleanup-ids', label: 'Clean up hard-coded user IDs', question: 'Delete the old hard-coded user IDs (like "demo-user-solo-demo-firm-solo") and point their email mappings at the real sign-in accounts?',
      job: async () => { const d = await call('/system-admin/cleanup-hardcoded-users', 'POST'); return d.hardcodedUsers === 0 ? 'No hard-coded user IDs found' : `Cleaned ${d.cleaned?.length ?? 0} user IDs; ${d.emailMappingsUpdated ?? 0} email mappings updated`; },
    },
    {
      id: 'legacy', label: 'Clean legacy data', question: 'Remove the legacy "Scopify" company whose subdomain "scopify" conflicts with the reserved subdomains? This cannot be undone.',
      job: async () => { const d = await call('/system-admin/clean-legacy-data', 'POST'); return d.message || 'Legacy data cleaned'; },
    },
    {
      // Staging and demo only: the server refuses it in production
      id: 'reset-demo', label: 'Reset demo content', question: 'Delete ALL demo content (projects, templates, requests and the rest) but keep system admins, companies and users? Use it to re-seed demo data without recreating everything.',
      job: async () => { const d = await call('/bootstrap/reset-demo-content', 'POST'); return `Demo content reset: ${d.deletedCount ?? 0} items deleted; system admins and companies kept`; },
    },
    {
      id: 'debug-user', label: 'Debug my user record', question: null,
      job: async () => { const d = await call('/system-admin/debug-user', 'GET'); return `My record: ${JSON.stringify(d).slice(0, 300)}`; },
    },
  ];

  return (
    <Section icon={Wrench} title="Platform jobs" description="One-off maintenance: template seeding, user-ID cleanup, the legacy Scopify company, demo content (not in production), your own record">
      <div className="flex flex-wrap gap-2">
        {jobs.map((j) => (
          <Button key={j.id} variant="outline" onClick={() => run(j.id, j.question, j.job)} disabled={running !== null}>
            {running === j.id ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Play className="size-4 mr-2" />}{j.label}
          </Button>
        ))}
      </div>
      {log.length > 0 && <pre className="rounded bg-gray-50 p-3 whitespace-pre-wrap" data-testid="jobs-log">{log.join('\n')}</pre>}
    </Section>
  );
}

function PromoteConfigItems({ accessToken }: { accessToken: string }) {
  const [items, setItems] = useState<any[] | null>(null);
  const [phase, setPhase] = useState('2');
  const load = async () => {
    const res = await fetch(`${API}/admin/unrecognized-config-items`, { headers: getAuthHeaders(accessToken) });
    const body = res.ok ? await res.json() : {};
    setItems(Array.isArray(body.unrecognizedItems) ? body.unrecognizedItems : []);
  };
  useEffect(() => { void load(); }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps
  const promote = async (item: any) => {
    const res = await fetch(`${API}/admin/promote-config-item`, {
      method: 'POST', headers: getAuthHeadersWithJson(accessToken),
      body: JSON.stringify({ itemName: item.itemName, category: item.category, description: item.description, defaultPhase: Number(phase) }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(body.error || 'Could not promote the item'); return; }
    toast.success(`"${item.itemName}" added to the global defaults`);
    void load();
  };
  return (
    <Section icon={Sparkles} title="New config items" description="Items companies added that are not in the global defaults; promote one so every new company gets it">
      <div className="flex items-center gap-2">
        <Label htmlFor="promote-phase">Default phase for promoted items</Label>
        <Select value={phase} onValueChange={setPhase}>
          <SelectTrigger id="promote-phase" className="w-24" aria-label="Default phase"><SelectValue /></SelectTrigger>
          <SelectContent>{['1', '2', '3', '4'].map((p) => <SelectItem key={p} value={p}>Phase {p}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {items === null ? <p className="text-muted-foreground">Loading…</p> : items.length === 0 ? <p className="text-muted-foreground">Nothing to promote.</p> : (
        <ul className="divide-y rounded border" data-testid="unrecognized-items">
          {items.map((item) => (
            <li key={`${item.category}:${item.itemName}`} className="flex items-center justify-between gap-3 p-3">
              <div>
                <p>{item.itemName} <Badge variant="outline" className="ml-1">{item.category}</Badge></p>
                <p className="text-muted-foreground">In {item.occurrences} {item.occurrences === 1 ? 'company' : 'companies'}{item.detectedInCompany ? `, first in ${item.detectedInCompany}` : ''}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => promote(item)}>Promote to global</Button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function CompanyMigration({ accessToken }: { accessToken: string }) {
  const [companies, setCompanies] = useState<any[] | null>(null);
  const load = async () => {
    const res = await fetch(`${API}/admin/companies-needing-migration`, { headers: getAuthHeaders(accessToken) });
    const body = res.ok ? await res.json() : {};
    setCompanies(Array.isArray(body.companies) ? body.companies : []);
  };
  useEffect(() => { void load(); }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps
  const migrate = async (c: any) => {
    if (!window.confirm(`Migrate "${c.name}" with a default SOW and config items?`)) return;
    const res = await fetch(`${API}/admin/migrate-company/${encodeURIComponent(c.id)}`, { method: 'POST', headers: getAuthHeaders(accessToken) });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(body.error || 'Migration failed'); return; }
    toast.success(`Migrated "${c.name}"`);
    void load();
  };
  return (
    <Section icon={Database} title="Companies needing setup" description="Companies created before engagements and config items existed; migrate gives them a default SOW and items (delete one from Companies › Edit)">
      {companies === null ? <p className="text-muted-foreground">Loading…</p> : companies.length === 0 ? <p className="text-muted-foreground">Every company is set up.</p> : (
        <ul className="divide-y rounded border" data-testid="migration-list">
          {companies.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 p-3">
              <div><p>{c.name}</p>{c.reason && <p className="text-muted-foreground">{c.reason}</p>}</div>
              <Button size="sm" variant="outline" onClick={() => migrate(c)}>Migrate</Button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function RecordsAndOrphans({ accessToken }: { accessToken: string }) {
  const [report, setReport] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/data-integrity`, { headers: getAuthHeaders(accessToken) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not check the records');
      setReport(body);
    } catch (e: any) { toast.error(e?.message || 'Could not check the records'); } finally { setBusy(false); }
  };
  const list = (title: string, rows: any[], line: (r: any) => string) => rows.length > 0 && (
    <div><p>{title} ({rows.length})</p><ul className="list-disc pl-5 text-muted-foreground">{rows.slice(0, 50).map((r, i) => <li key={i}>{line(r)}</li>)}</ul></div>
  );
  return (
    <Section icon={ShieldAlert} title="Records and orphans" description="Counts of companies, firms and people, and what does not hang together. Read-only.">
      <Button variant="outline" onClick={run} disabled={busy}>{busy ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Search className="size-4 mr-2" />}Run check</Button>
      {report && (
        <div className="space-y-3" data-testid="integrity-report">
          <div className="flex flex-wrap gap-2">
            {Object.entries(report.counts || {}).map(([k, v]) => <Badge key={k} variant="secondary">{k}: {String(v)}</Badge>)}
          </div>
          {report.issueCount === 0 ? <Alert><AlertDescription>No orphans or mismatches found.</AlertDescription></Alert> : (
            <>
              {list('Companies without a firm', report.orphanedCompanies || [], (r) => `${r.name}: ${r.reason}`)}
              {list('Firms without a subdomain', report.firmsMissingSubdomain || [], (r) => r.name)}
              {list('Subdomain index mismatches', report.subdomainMismatches || [], (r) => `${r.subdomain} → ${r.indexedFirmId ?? 'nothing'} (should be ${r.firmName})`)}
              {list('People with neither a firm nor a company', report.usersWithoutHome || [], (r) => r.email)}
            </>
          )}
        </div>
      )}
    </Section>
  );
}

export function SystemAdminOperations({ accessToken }: { accessToken: string }) {
  return (
    <div className="space-y-6" data-testid="system-admin-operations">
      <AccountRepair accessToken={accessToken} />
      <RecordsAndOrphans accessToken={accessToken} />
      <PromoteConfigItems accessToken={accessToken} />
      <CompanyMigration accessToken={accessToken} />
      <PlatformJobs accessToken={accessToken} />
      <Section icon={Database} title="Corrupted records" description="Scan for records that cannot be read and remove them">
        <DataCleanupTool />
      </Section>
      <Section icon={Wrench} title="Subdomains and Vercel" description="Create a subdomain by hand and check the Vercel configuration">
        <ManualSubdomainCreator />
      </Section>
      <AIUsageMonitor accessToken={accessToken} />
      <Section icon={KeyRound} title="This browser's session" description="What PathKit keeps in this browser's storage, for sign-in problems">
        <SessionDebugger />
      </Section>
    </div>
  );
}
